import React, { useCallback, useEffect, useState } from 'react';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { fetchToken } from '../../app/helpers/fetch';
import { const_routes } from '../../app/helpers/const_routes';

const MODULE_KEYS = [
  'technology',
  'alternative',
  'experience',
  'innovation',
  'assistance',
  'financial',
  'vision',
  'regulation',
];

const MODULE_ID_BY_KEY = {
  technology: 1,
  alternative: 2,
  experience: 3,
  innovation: 4,
  assistance: 5,
  financial: 6,
  vision: 7,
  regulation: 8,
};

const ACTIVE_JOB_STATUSES = ['queued', 'running'];

const jobModuleKey = (job) => {
  if (job?.module_key) return job.module_key;
  const id = Number(job?.module_id);
  return MODULE_KEYS.find((key) => MODULE_ID_BY_KEY[key] === id) || null;
};

const isActiveJob = (job) => ACTIVE_JOB_STATUSES.includes(String(job?.status || ''));

const findActiveJobForModule = (jobs, moduleKey) => {
  const moduleId = MODULE_ID_BY_KEY[moduleKey];
  return (jobs || []).find((job) => {
    if (!isActiveJob(job)) return false;
    const key = jobModuleKey(job);
    return key === moduleKey || Number(job.module_id) === moduleId;
  }) || null;
};

const jobProgressPercent = (job) => {
  if (!job) return 0;
  if (job.percent_complete != null && job.percent_complete !== '') {
    return Number(job.percent_complete);
  }
  return String(job.status) === 'done' ? 100 : 0;
};

const parseAuthError = (body) => {
  if (body && (body.code === 'bak-aut-tok-001' || body.code === 'bak-aut-tok-002')) return body;
  return null;
};

const AdminSemanticJobsScreen = () => {
  const { t } = useTranslation();
  const [jobs, setJobs] = useState([]);
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busyKey, setBusyKey] = useState('');

  const loadJobs = useCallback(async () => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_SEMANTIC,
      'jobs?limit=40'
    );
    const body = await resp.json();
    if (parseAuthError(body)) return;
    if (body.ok) setJobs(body.jobs || []);
  }, []);

  const loadCoverage = useCallback(async () => {
    const resp = await fetchToken(
      const_routes.SCHEMA,
      const_routes.VERSIONV1,
      const_routes.ROUTE_SEMANTIC,
      'coverage/all'
    );
    const body = await resp.json();
    if (parseAuthError(body)) return;
    if (body.ok) setCoverage(body.modules || null);
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadJobs(), loadCoverage()]);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [loadCoverage, loadJobs]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    const hasActive = (jobs || []).some(isActiveJob);
    if (!hasActive) return undefined;
    const timer = setInterval(() => {
      loadJobs();
      loadCoverage();
    }, 2000);
    return () => clearInterval(timer);
  }, [jobs, loadCoverage, loadJobs]);

  const startReprocess = async (moduleKey) => {
    const label = moduleKey === 'all'
      ? t('semanticSearch.jobs.reprocessAll', { defaultValue: 'los 8 módulos' })
      : t(`semanticSearch.module.${moduleKey}`);
    const confirm = await Swal.fire({
      title: t('semanticSearch.jobs.confirmTitle', { defaultValue: '¿Reprocesar embeddings?' }),
      text: t('semanticSearch.jobs.confirmText', {
        defaultValue: 'Se encolará vectorización completa de {{label}}. Los vectores actuales se irán reemplazando.',
        label,
      }),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: t('semanticSearch.jobs.confirmOk', { defaultValue: 'Lanzar' }),
      cancelButtonText: t('semanticSearch.jobs.cancel', { defaultValue: 'Cancelar' }),
    });
    if (!confirm.isConfirmed) return;

    setBusyKey(moduleKey);
    try {
      const resp = await fetchToken(
        const_routes.SCHEMA,
        const_routes.VERSIONV1,
        const_routes.ROUTE_SEMANTIC,
        `reprocess/${moduleKey}`,
        {},
        'POST'
      );
      const body = await resp.json();
      if (parseAuthError(body)) return;
      if (body.ok === false && body.code === 'bak-error-001') {
        Swal.fire('Error', body.msg || 'Error al encolar', 'error');
        return;
      }
      if (body.ok) {
        const count = body.count || 1;
        Swal.fire(
          t('semanticSearch.jobs.queuedTitle', { defaultValue: 'Encolado' }),
          t('semanticSearch.jobs.queuedText', {
            defaultValue: '{{count}} job(s) en cola. El worker procesa en segundo plano.',
            count,
          }),
          'success'
        );
        await refresh();
      }
    } catch (err) {
      console.error(err);
      Swal.fire('Error', String(err.message || err), 'error');
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div className="container mt-4">
      <h2>{t('semanticSearch.jobs.title', { defaultValue: 'Jobs de vectorización' })}</h2>
      <p className="text-muted">
        {t('semanticSearch.jobs.hint', {
          defaultValue: 'Tras reparametrizar campos, lanza un reproceso por módulo o de los 8. Requiere semantic.search.enabled y el flag del módulo en true, y el embedder en :8090.',
        })}
      </p>
      <div className="mb-3 d-flex flex-wrap gap-2">
        <button
          type="button"
          className="btn btn-primary"
          disabled={!!busyKey || (jobs || []).some(isActiveJob)}
          onClick={() => startReprocess('all')}
        >
          {t('semanticSearch.jobs.reprocessAllBtn', { defaultValue: 'Reprocesar los 8 módulos' })}
        </button>
        <button type="button" className="btn btn-outline-secondary" disabled={loading} onClick={refresh}>
          {t('semanticSearch.jobs.refresh', { defaultValue: 'Actualizar' })}
        </button>
      </div>

      <h4>{t('semanticSearch.jobs.coverage', { defaultValue: 'Cobertura' })}</h4>
      <p className="text-muted small">
        {t('semanticSearch.jobs.coverageHint', {
          defaultValue: 'Padres / Ready es la cobertura real (vectores listos). Si hay un reproceso en cola o en curso, la columna % de esa fila se encera y muestra el avance del job (0→100), no la cobertura.',
        })}
      </p>
      <div className="table-responsive mb-4">
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              <th>Módulo</th>
              <th>Padres</th>
              <th>Ready</th>
              <th>Error</th>
              <th>%</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {MODULE_KEYS.map((key) => {
              const row = coverage?.[key] || {};
              const activeJob = findActiveJobForModule(jobs, key);
              const displayPercent = activeJob
                ? jobProgressPercent(activeJob)
                : row.coveragePercent;
              const jobLabel = activeJob
                ? (String(activeJob.status) === 'queued'
                  ? t('semanticSearch.jobs.jobQueued', { defaultValue: 'en cola' })
                  : t('semanticSearch.jobs.jobRunning', { defaultValue: 'en curso' }))
                : t('semanticSearch.jobs.percentCoverage', { defaultValue: 'cobertura' });
              return (
                <tr key={key} className={activeJob ? 'table-warning' : undefined}>
                  <td>{t(`semanticSearch.module.${key}`)}</td>
                  <td>{row.parentTotal ?? '—'}</td>
                  <td>{row.embeddingsReady ?? '—'}</td>
                  <td>{row.embeddingsError ?? '—'}</td>
                  <td style={{ minWidth: 160 }}>
                    {displayPercent != null ? (
                      <>
                        {activeJob && (
                          <div className="progress mb-1" style={{ height: 8 }}>
                            <div
                              className="progress-bar progress-bar-striped progress-bar-animated"
                              role="progressbar"
                              style={{ width: `${Math.max(0, Math.min(100, displayPercent))}%` }}
                              aria-valuenow={displayPercent}
                              aria-valuemin={0}
                              aria-valuemax={100}
                            />
                          </div>
                        )}
                        <div>
                          <strong>{`${displayPercent}%`}</strong>
                          {' '}
                          <span className="text-muted small">{jobLabel}</span>
                        </div>
                        {activeJob && row.coveragePercent != null && (
                          <div className="text-muted small">
                            {t('semanticSearch.jobs.percentCoverage', { defaultValue: 'cobertura' })}
                            {`: ${row.coveragePercent}%`}
                          </div>
                        )}
                      </>
                    ) : '—'}
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      disabled={!!busyKey || !!activeJob}
                      onClick={() => startReprocess(key)}
                    >
                      {t('semanticSearch.jobs.reprocessOne', { defaultValue: 'Reprocesar' })}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <h4>{t('semanticSearch.jobs.recent', { defaultValue: 'Jobs recientes' })}</h4>
      <div className="table-responsive">
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              <th>ID</th>
              <th>Módulo</th>
              <th>Tipo</th>
              <th>Estado</th>
              <th>{t('semanticSearch.jobs.colProgress', { defaultValue: 'Avance' })}</th>
              <th>{t('semanticSearch.jobs.colProcessed', { defaultValue: 'Procesados / error' })}</th>
            </tr>
          </thead>
          <tbody>
            {(jobs || []).length === 0 && (
              <tr><td colSpan={6} className="text-muted">Sin jobs</td></tr>
            )}
            {(jobs || []).map((job) => {
              const key = jobModuleKey(job);
              const pct = jobProgressPercent(job);
              const statusKey = `semanticSearch.jobs.status${String(job.status || '').charAt(0).toUpperCase()}${String(job.status || '').slice(1)}`;
              const statusLabel = t(statusKey, { defaultValue: job.status });
              const total = Number(job.total_items || 0);
              const processed = Number(job.processed_items || 0);
              const failed = Number(job.failed_items || 0);
              return (
                <tr key={job.id} className={isActiveJob(job) ? 'table-warning' : undefined}>
                  <td>{job.id}</td>
                  <td>{key ? t(`semanticSearch.module.${key}`) : job.module_id}</td>
                  <td>{job.job_type}</td>
                  <td>{statusLabel}</td>
                  <td style={{ minWidth: 140 }}>
                    {isActiveJob(job) && (
                      <div className="progress mb-1" style={{ height: 8 }}>
                        <div
                          className="progress-bar progress-bar-striped progress-bar-animated"
                          role="progressbar"
                          style={{ width: `${Math.max(0, Math.min(100, pct))}%` }}
                        />
                      </div>
                    )}
                    {`${pct}%`}
                  </td>
                  <td>{total > 0 ? `${processed + failed} / ${total} (${failed} err)` : `${processed} / ${failed}`}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AdminSemanticJobsScreen;
